"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sparkles, Sun, CalendarCheck, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Pilgrimage Surges & Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage extreme weekend and festival demand peaks alongside rapid same-week booking spikes. We establish dynamic minimum length-of-stay controls and pace-based pricing so your Dwarka property captures maximum RevPAR without relying on last-minute discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pacing & sacred calendar rate rules"
}, {
  id: 2,
  title: "Local Comp-Set & Walk-In Price Pressures",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Goibibo, Agoda, and Booking.com. Automated competitive alerts and dynamic yield rules protect your margins while capturing high-intent family and pilgrimage bookings before market troughs.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim revenue lost to high OTA commissions. We enforce strict rate parity across meta-search channels and drive direct pilgrim bookings through verified package incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking conversion"
}];
const seasonalityWindows = [{
  id: "peak",
  season: "Peak Pilgrimage & Winter",
  period: "October – March",
  demandLevel: "Maximum Demand (90%+ Occupancy)",
  icon: Sparkles,
  strategy: "Premium ADR positioning, 2-night minimum length-of-stay (MLOS) rules, and tight cancellation policies.",
  badgeClass: "bg-secondary text-secondary-foreground"
}, {
  id: "festival",
  season: "Mega-Festival Surges",
  period: "Janmashtami & Diwali Week",
  demandLevel: "Hyper Peak (100% Demand Surge)",
  icon: CalendarCheck,
  strategy: "Dynamic high-yield surge rate fences, non-refundable pre-paid packages, and high-tier direct booking prioritization.",
  badgeClass: "bg-primary text-primary-foreground"
}, {
  id: "summer",
  season: "Summer & Monsoon Shoulder",
  period: "April – September",
  demandLevel: "Moderate to Value-Driven Inflow",
  icon: Sun,
  strategy: "Corporate pilgrim groups, local family packages, breakfast-inclusive bundle pricing, and selective OTA flash promotions.",
  badgeClass: "bg-muted text-foreground border border-border"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapedwarka" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Dwarka
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Dwarka represents one of India's most vibrant sacred pilgrimage hubs, characterized by sharp spiritual festival spikes, compressed booking lead times, and intense competition among dharamshalas, independent hotels, and boutique retreats. Sunil Singh structures disciplined, data-backed revenue strategies engineered specifically for Dwarka's unique spiritual and coastal hospitality market.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="mt-16 md:mt-20 rounded-xl border border-border bg-card p-6 md:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-2 text-secondary text-xs font-semibold uppercase tracking-wider mb-2">
                <TrendingUp className="size-4" />
                <span>Demand Modeling</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                Dwarka Hotel Seasonality & Yield Calendar
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Optimizing rate thresholds across religious calendar fluctuations, temple timing peaks, and regional vacation corridors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {seasonalityWindows.map((seasonItem, i) => {
            const IconComp = seasonItem.icon;
            return <div key={seasonItem.id} data-index={i} className="rounded-lg border border-border/80 bg-background/60 p-5 flex flex-col justify-between space-y-4 hover:border-secondary/40 transition-[border-color] duration-300">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                        <IconComp className="size-4" />
                      </span>
                      <span className="text-xs font-mono font-medium text-muted-foreground">
                        {seasonItem.period}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-serif text-lg font-bold text-foreground">
                        {seasonItem.season}
                      </h4>
                      <p className="text-xs font-semibold text-secondary mt-0.5">
                        {seasonItem.demandLevel}
                      </p>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-border/60 text-xs text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground block mb-1">Yield Strategy:</span>
                    {seasonItem.strategy}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Dwarka hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}